import { type MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Alert } from 'react-bootstrap';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';

import { PageHeading } from '../components/common/PageHeading';
import { StoreIcon, TabletIcon, ChartIcon } from '../components/icons/ServiceIcons';
import { post } from '../lib/api';
import { ApplicationType } from '../lib/applications';
import { useAuthState } from '../auth/AuthContext';
import { useTokenEmail } from '../auth/useTokenEmail';
import { isAdUser } from '../auth/roles';
import { usePageTitle } from '../lib/usePageTitle';

interface Option {
    value: string;
    label: string;
}

export function HomePage() {
    const { t } = useTranslation();
    const auth = useAuthState();
    const isAd = isAdUser(auth);
    usePageTitle(); // reset to the base title after pages that set their own

    const [applications, setApplications] = useState<Option[]>([]);
    const [jasperApps, setJasperApps] = useState<Option[]>([]);
    const [servicesLoaded, setServicesLoaded] = useState(false);
    const [selectedApp, setSelectedApp] = useState('');
    const [selectedJasperApp, setSelectedJasperApp] = useState('');
    const [selectionError, setSelectionError] = useState('');
    const [jasperSelectionError, setJasperSelectionError] = useState('');
    const loggedInUser = useTokenEmail();

    // Derived from selectedApp rather than hand-synced on every mount/pick/change.
    // Local (non-AD) users must launch with an EMPTY app so the backend resolves
    // their single mapping from the claim (ProcessLoading's claim branch); only
    // AD users pin the chosen instance name into the href.
    const normalizedSmartstore = useMemo(() => selectedApp.replace(/\s+/g, ''), [selectedApp]);
    const smartstoreHref = useMemo(() => {
        if (!auth.token) return '';
        const app = isAd ? selectedApp : '';
        return `/Home/GetSmartStore?selectedApp=${app.replace(/\s+/g, '%20')}&token=${auth.token}`;
    }, [auth.token, isAd, selectedApp]);

    const winRefs = useRef<Record<string, Window | null>>({});
    const instanceCount = useRef(1);

    useEffect(() => {
        if (!isAd) {
            setSelectedApp('smartstore');
            setSelectedJasperApp('jasper');
        }

        // Tiles are driven by the user's *actual* mappings: only the services a
        // user is mapped to are shown (none -> placeholder). Wait for both
        // lookups so the placeholder never flashes before the data arrives.
        Promise.all([
            loadApplicationNames(ApplicationType.Smartstore),
            loadApplicationNames(ApplicationType.Jasper),
        ]).finally(() => setServicesLoaded(true));
        // Intentionally run once on mount, mirroring the legacy page lifecycle.
    }, []);

    function loadApplicationNames(type: ApplicationType) {
        return post<string[]>('home/getUserApplicationNames', { appType: type }).then(
            (res) => {
                if (res.status !== 200) return;
                const options = (res.data ?? []).map((d) => ({ value: d, label: d })).sort((a, b) => a.label.localeCompare(b.label, 'nb'));
                if (type === ApplicationType.Smartstore) {
                    setApplications(options);
                    // AD users pick an instance; if there is only one, preselect
                    // it so they don't have to choose before launching.
                    if (isAd && options.length === 1) {
                        setSelectedApp(options[0].value);
                    }
                } else {
                    setJasperApps(options);
                    if (isAd && options.length === 1) {
                        setSelectedJasperApp(options[0].value);
                    }
                }
            },
            () => {
                /* ignore */
            },
        );
    }

    function detectMobile(): boolean {
        return [/Android/i, /webOS/i, /iPhone/i, /iPad/i, /iPod/i, /BlackBerry/i, /Windows Phone/i].some(
            (re) => navigator.userAgent.match(re),
        );
    }

    function handleSmartstoreChange(option: Option | null) {
        if (!option) return;
        setSelectedApp(option.value);
        setSelectionError('');
        setJasperSelectionError('');
    }

    function handleJasperChange(option: Option | null) {
        if (!option) return;
        setSelectedJasperApp(option.value);
        setSelectionError('');
        setJasperSelectionError('');
    }

    function launch(windowName: string, appType: ApplicationType) {
        if (appType !== ApplicationType.Jasper && !selectedApp.length) {
            setSelectionError(t('home.selectInstallationFirst'));
            return;
        }
        if (appType === ApplicationType.Jasper && !selectedJasperApp.length) {
            setJasperSelectionError(t('home.selectReportingFirst'));
            return;
        }

        const target = appType === ApplicationType.MinButtik ? `${windowName}dknMinbutikk` : windowName;
        const existing = winRefs.current[target];

        if (existing && !existing.closed) {
            existing.focus();
            return;
        }

        let form: HTMLFormElement | null = null;
        let redirectForm: HTMLFormElement | null = null;

        if (appType === ApplicationType.Smartstore) {
            form = document.getElementById('dekonForm') as HTMLFormElement | null;
            if (form) form.action = 'Home/LoadSmartStore';
        } else if (appType === ApplicationType.MinButtik) {
            form = document.getElementById('dekonForm') as HTMLFormElement | null;
            if (form) form.action = 'Home/LoadMinbutikk';
        } else if (appType === ApplicationType.Jasper) {
            form = document.getElementById('dekonJasperForm') as HTMLFormElement | null;
            redirectForm = document.getElementById('dekonJasperRedirectForm') as HTMLFormElement | null;
            if (form) form.action = 'Home/LoadJasper';
        }

        if (!form) return;
        form.target = target;
        winRefs.current[target] = window.open('', target);
        form.submit();

        if (redirectForm) {
            redirectForm.target = target;
            setTimeout(() => redirectForm?.submit(), 1000);
        }
    }

    function goToMinbutikk() {
        launch(selectedApp, ApplicationType.MinButtik);
    }

    function goToJasper(e: MouseEvent) {
        let name = selectedJasperApp.replace(/\s+/g, '');
        if (e.ctrlKey || e.metaKey) {
            while (winRefs.current[name] && !winRefs.current[name]?.closed) {
                instanceCount.current += 1;
                name = `${selectedJasperApp}${instanceCount.current}`;
            }
        }
        launch(name, ApplicationType.Jasper);
    }

    // The SmartStore link opens via a real GET href into a named window; this
    // guards selection and reuses/focuses an existing window.
    function smartstoreLinkClick(e: MouseEvent) {
        if (!selectedApp.length) {
            e.preventDefault();
            setSelectionError(t('home.selectInstallationFirst'));
            return;
        }
        const name = normalizedSmartstore || selectedApp.replace(/\s+/g, '');
        const existing = winRefs.current[name];
        if (e.ctrlKey || e.metaKey || detectMobile() || !existing || existing.closed) {
            setTimeout(() => {
                winRefs.current[name] = window.open('', name);
            }, 200);
        } else {
            e.preventDefault();
            existing.focus();
        }
    }

    const hasSmartStore = applications.length > 0;
    const hasJasper = jasperApps.length > 0;

    return (
        <div>
            <PageHeading
                title={t('home.title')}
                description={t('home.description')}
                meta={loggedInUser ? `${t('home.welcome')}, ${loggedInUser}` : undefined}
            />

            {servicesLoaded && !hasSmartStore && !hasJasper && (
                <Alert variant="info">{t('home.noServices')}</Alert>
            )}

            {(hasSmartStore || hasJasper) && (
                <div className="row g-3 g-lg-4 justify-content-center">
                    {hasSmartStore && (
                        <div className="col-12 col-lg-6">
                            <section className="service-card" aria-labelledby="ss-card-title">
                                <h2 id="ss-card-title" className="service-card__title">
                                    {t('home.smartstoreGroup')}
                                </h2>
                                {isAd && applications.length > 1 && (
                                    <div className="service-card__picker">
                                        <Select<Option>
                                            inputId="smartstore-select"
                                            aria-label={t('home.smartstoreGroup')}
                                            options={applications}
                                            value={applications.find((o) => o.value === selectedApp) ?? null}
                                            onChange={handleSmartstoreChange}
                                            classNamePrefix="dkn-rs"
                                            placeholder={t('common.selectPlaceholder')}
                                            noOptionsMessage={() => t('common.noOptions')}
                                        />
                                    </div>
                                )}
                                <div className="service-grid">
                                    <div onClick={smartstoreLinkClick}>
                                        <a
                                            className="service-tile"
                                            href={smartstoreHref}
                                            target={normalizedSmartstore || 'smartstore'}
                                        >
                                            <StoreIcon className="service-tile__icon" />
                                            <span className="service-tile__label">{t('home.smartstore')}</span>
                                        </a>
                                    </div>

                                    <button type="button" className="service-tile" onClick={goToMinbutikk}>
                                        <TabletIcon className="service-tile__icon" />
                                        <span className="service-tile__label">{t('home.minButikk')}</span>
                                    </button>
                                </div>
                                {selectionError && (
                                    <div className="text-danger small mt-2">{selectionError}</div>
                                )}
                            </section>
                        </div>
                    )}

                    {hasJasper && (
                        <div className="col-12 col-lg-6">
                            <section className="service-card" aria-labelledby="reporting-card-title">
                                <h2 id="reporting-card-title" className="service-card__title">
                                    {t('home.reportingGroup')}
                                </h2>
                                {isAd && jasperApps.length > 1 && (
                                    <div className="service-card__picker">
                                        <Select<Option>
                                            inputId="reporting-select"
                                            aria-label={t('home.reportingGroup')}
                                            options={jasperApps}
                                            value={jasperApps.find((o) => o.value === selectedJasperApp) ?? null}
                                            onChange={handleJasperChange}
                                            classNamePrefix="dkn-rs"
                                            placeholder={t('common.selectPlaceholder')}
                                            noOptionsMessage={() => t('common.noOptions')}
                                        />
                                    </div>
                                )}
                                <div className="service-grid">
                                    <button type="button" className="service-tile" onClick={goToJasper}>
                                        <ChartIcon className="service-tile__icon" />
                                        <span className="service-tile__label">{t('home.reporting')}</span>
                                    </button>
                                </div>
                                {jasperSelectionError && (
                                    <div className="text-danger small mt-2">{jasperSelectionError}</div>
                                )}
                            </section>
                        </div>
                    )}
                </div>
            )}

            <form id="dekonForm" method="post" action="Home/LoadSmartStore" target={selectedApp}>
                <input type="hidden" name="jwtToken" value={auth.token} />
                {isAd && <input type="hidden" name="selectedApp" value={selectedApp} />}
            </form>
            <form id="dekonJasperForm" method="post" action="Home/LoadJasper" target={selectedJasperApp}>
                <input type="hidden" name="jwtToken" value={auth.token} />
                {isAd && <input type="hidden" name="selectedApp" value={selectedJasperApp} />}
            </form>
            <form id="dekonJasperRedirectForm" method="post" action="Home/RedirectJasper" target={selectedJasperApp}>
                <input type="hidden" name="jwtToken" value={auth.token} />
                {isAd && <input type="hidden" name="selectedJasperApp" value={selectedJasperApp} />}
            </form>
        </div>
    );
}
